import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Observable";
import { ApiService } from "@edp-services/api.service";
import { Onboarding, OnboardingWhatsNew } from "@edp-models/onboarding.model";
import { environment } from "@edp-environments/environment";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import { GuidedTour } from "@edp-components/ngx-guided-tour/guided-tour.constants";
import { Store, select } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { SetCompleteOnboarding } from "@edp-store/actions/onboarding.actions";
import moment from "moment";
import { OnboardingTriggerUserEnum } from "@edp-enums/onboarding-trigger-user.enum";
import { OnboardingTypeEnum } from "@edp-enums/onboarding-type.enum";
import { OperationHistoryService } from "./operation-history.service";
import { filter } from "rxjs/operators";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { of } from "rxjs";

const operationHistoryOnboardingType = {
    Discover: "DISCOVER",
    Intro: "ONBOARDING_INTRO",
    StepByStep: "STEP_BY_STEP",
    Tooltip: "TOOLTIP",
    WhatsNew: "WHATSNEW",
};

const operationHistoryOnboarding = {
    VIEW: "VIEW_{TYPE}",
    CLICKED: "CLICKED_{TYPE}",
    NEXT: "CLICKED_{TYPE}_NEXT",
    FINISH: "CLICKED_{TYPE}_FINISH",
    CLOSE: "CLICKED_{TYPE}_CLOSE",
    OUTSIDE: "CLICKED_{TYPE}_OUTSIDE_CLOSE",
};

const parmApplication = {
    desktop: 0,
    mobile: 1,
    app: 2,
};

const keySessionSentWhatsNew = "sent-reading-whatsnew";
const keySessionSentTriggers = "sent-onboarding-triggers";

interface ISetTriggerUser {
    triggerName: OnboardingTriggerUserEnum;
    dateStart: string;
    async: boolean;
}

@Injectable({
    providedIn: "root",
})
export class OnboardingService {
    public contractId: string;

    constructor(
        private store: Store<IAppState>,
        private apiService: ApiService,
        private readonly operationHistoryService: OperationHistoryService
    ) {
        this.getContract();
    }

    private getContract(): void {
        this.store
            .pipe(
                select(getContract),
                filter(val => val !== null && val !== undefined)
            )
            .subscribe(contract => {
                this.contractId = contract.ContractId;
            });
    }

    public getOnboarding(id: string, steps: Array<any>, type: OnboardingTypeEnum): GuidedTour {
        const tour: GuidedTour = {
            tourId: id,
            steps: steps.map(({ anchor, title, description, image, anchorParentComponent }) => {
                return {
                    selector: anchor,
                    content: description,
                    title,
                    image,
                    anchorParentComponent,
                };
            }),
            type,
            completeCallback: () => this.store.dispatch(new SetCompleteOnboarding(id)),
        };

        return tour;
    }

    private getParmApplication() {
        return environment.isApp
            ? parmApplication.app
            : window.innerWidth <= SCREEN_SIZES.MD
            ? parmApplication.mobile
            : parmApplication.desktop;
    }

    public getTourWhatsNew(): Observable<Error | OnboardingWhatsNew | Array<any>> {
        return this.apiService.callServicePostToCollection("onboarding.getWhatsNew", { mobile: this.getParmApplication() });
    }

    public getTourGuide(): Observable<Error | Onboarding | Array<any>> {
        return this.apiService.callServicePostToCollection("onboarding.getTourGuide", { mobile: this.getParmApplication() });
    }

    public setInteract(onboardingId: string, action: "reading" | "complete" = "complete"): Observable<Error | any> {
        if (action === "reading") {
            this.sendReadingWhatsNew(onboardingId);
        }
        return this.apiService.callServicePostToCollection("onboarding.setInteract", { onboardingId, action });
    }

    public sendReadingWhatsNew(onboardingId: string): void {
        const storage = sessionStorage.getItem(keySessionSentWhatsNew);
        const array: Array<string> = storage ? JSON.parse(storage) : [];
        array.push(onboardingId);
        sessionStorage.setItem(keySessionSentWhatsNew, JSON.stringify(array));
    }

    public getReadingWhatsNew(onboardingId): boolean {
        const storage: Array<string> = JSON.parse(sessionStorage.getItem(keySessionSentWhatsNew));
        return !!storage && !!storage.find(i => i === onboardingId);
    }

    private getDateStartOnboardingTrigger(trigger: OnboardingTriggerUserEnum) {
        let date: string;
        switch (trigger) {
            case OnboardingTriggerUserEnum.SELECTOR_CONSUMPTION_1M:
                date = moment()
                    .add(1, "months")
                    .format("YYYY-MM-DD");
                break;
            case OnboardingTriggerUserEnum.YOUR_LOCAL_6M:
                date = moment()
                    .add(6, "months")
                    .format("YYYY-MM-DD");
                break;
            case OnboardingTriggerUserEnum.CONSUMPTION_3M:
            case OnboardingTriggerUserEnum.ACESS_PAYMENT_3M:
            case OnboardingTriggerUserEnum.PROFILE_3M:
            case OnboardingTriggerUserEnum.YOUR_LOCAL_3M:
                date = moment()
                    .add(3, "months")
                    .format("YYYY-MM-DD");
                break;
        }
        return date;
    }

    private userTriggerAction(action: OnboardingTriggerUserEnum): ISetTriggerUser {
        return {
            async: true,
            dateStart: this.getDateStartOnboardingTrigger(action),
            triggerName: action,
        };
    }

    private checkSessionStorageTriggersSent(action: OnboardingTriggerUserEnum): boolean {
        const triggersSent: Array<string> = JSON.parse(sessionStorage.getItem(keySessionSentTriggers)) || [];
        if (triggersSent.length > 0 && triggersSent.find(i => i === action)) {
            return true;
        }
        triggersSent.push(action);
        sessionStorage.setItem(keySessionSentTriggers, JSON.stringify(triggersSent));
        return false;
    }

    public registerUserAction(action: OnboardingTriggerUserEnum): Observable<any> {
        if (!!this.checkSessionStorageTriggersSent(action) && !environment.isApp) {
            return of();
        }
        const args: ISetTriggerUser = this.userTriggerAction(action);
        return this.apiService.callServicePostToCollection("onboarding.setTriggerUser", args);
    }

    public setoperationHistoryService(
        onboardingId: string,
        type: OnboardingTypeEnum,
        key: "VIEW" | "CLICKED" | "NEXT" | "FINISH" | "CLOSE" | "OUTSIDE"
    ): void {
        const operationOnboardingType = operationHistoryOnboardingType[type];
        if (!operationOnboardingType) {
            return;
        }
        const operation = operationHistoryOnboarding[key].replace("{TYPE}", operationOnboardingType);
        this.operationHistoryService.registerWithContract(this.contractId, operation, null, null, null, false, null, onboardingId);
    }
}
